/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, BackupModel, NamespaceModel, OrganisationModel } from '../models';
import { DashboardService } from '../services/component';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { LogService, NotifyService, RbacService, UtilsService } from '../../../../core/services';
import { NamespaceService, PortalRbacService } from '../services/utils';
import { Observable, Subscriber } from 'rxjs';
import { PortalMenu } from '../lists/portal.menu';
import { UserModel } from '../../../loadbalancer/shared';

@Injectable()
export class PortalResolverGuard {
    constructor(
        private dashboardService: DashboardService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private notifyService: NotifyService,
        private portalRbacService: PortalRbacService,
        private rbacService: RbacService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Can activate route.
     */
    public canActivate(): Observable<boolean> {
        return new Observable((observer: Subscriber<any>): void => {
            this.utilsService.debounce('canActivate', (): void => {
                this.notifyService.muteLocalNotifications();

                this.dashboardService.getServiceData()
                    .subscribe({
                        next: (data: any): void => {
                            this.registerDataWithServices(data);
                            const timerId = setTimeout((): void => {
                                this.notifyService.unmuteLocalNotifications();
                                clearTimeout(timerId);
                            }, 500); // Delay the un-muting of notifications.
                            observer.next(data);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('ResolverGuard: canActivate', 'Unable to resolve page data ', error);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            });
        });
    }

    /**
     * Register data with services.
     * @param data
     * @private
     */
    private registerDataWithServices(data: {
        adcs: AdcModel[];
        backups: BackupModel[];
        certs: any[];
        insights: any;
        namespaces: NamespaceModel[];
        organisation: OrganisationModel;
        rbac: any;
        resources: any;
        schedules: any;
        timeStamp: number
        users: UserModel[];
    }): void {

        const {
            adcs,
            backups,
            certs,
            endpoints,
            insights,
            namespaces,
            organisation,
            resources,
            schedules,
            tier,
            users,
        } = this.formatValuesInCaseOfError(data);

        this.rbacService.endpointsLookup = this.utilsService.sortArrayByProperty(endpoints, 'path');
        this.portalRbacService.resources = resources;
        this.portalRbacService.selectedTier = tier;
        this.rbacService.menus = PortalMenu;
        this.dashboardService.allData = {
            adcs,
            backups,
            certs,
            insights,
            namespaces,
            organisation,
            schedules,
            timeStamp: data.timeStamp,
            users,
        };

        // Set saml enforced in lb history to help with keeping the login page up to date.
        this.lbHistoryUpdate(organisation?.samlEnforced);
        this.namespaceService.namespaces = namespaces;
    }

    /**
     * Format values, return empty arrays and basic objects if a call has errored.
     * @param data
     * @private
     */
    private formatValuesInCaseOfError(data: any): any {
        let { adcs, backups, insights, organisation, namespaces, resources, schedules, users } = data;
        adcs = this.checkDataType(adcs, 'ADCs');
        backups = this.checkDataType(backups, 'backups');
        const endpoints: any[] = this.checkDataType(data?.rbac?.endpoints, 'endpoints');
        insights = this.checkDataType(insights, 'insights');
        organisation = organisation || this.getEmptyOrganisation();
        namespaces = this.checkDataType(namespaces, 'namespace');
        resources = resources || this.getBasicResources();
        schedules = this.checkDataType(schedules, 'schedules');
        const tier = organisation?.tier || 'free';
        users = this.checkDataType(users, 'users');

        return { adcs, backups, endpoints, insights, organisation, namespaces, resources, schedules, tier, users };
    }

    /**
     * Check data type.
     * @param data
     * @param responseName
     * @private
     */
    private checkDataType(data: any[], responseName: string): any[] {
        if (Array.isArray(data)) {
            if (data.length && !data[0].id && !data[0].path && !data[0].adcId) {
                this.logService.debug('ResolverGuard: checkDataType', `${responseName} API call didn't return an expected property.`);

                return [];
            }

            return data;
        } else {
            this.logService.debug('ResolverGuard: checkDataType', `${responseName} API call didn't return the expected data.`);

            return [];
        }
    }

    /**
     * Get basic resources.
     * @private
     */
    private getBasicResources(): any {
        return {
            'business': {
                'limits': { 'adc-connections': 0, 'backups-per-adc': 0 },
                'values': { 'support-level': 'best' }
            },
            'free': {
                'limits': { 'adc-connections': 0, 'backups-per-adc': 0 },
                'values': { 'support-level': 'basic' }
            },
            'professional': {
                'limits': { 'adc-connections': 0, 'backups-per-adc': 0 },
                'values': { 'support-level': 'better' }
            },
            'tailored': {
                'limits': { 'adc-connections': 0, 'backups-per-adc': 0 },
                'values': { 'support-level': 'best' }
            }
        };
    }

    /**
     * Get empty organisation
     * @private
     */
    private getEmptyOrganisation(): any {
        return {
            adoptionToken: '',
            contactNumber: '',
            id: '',
            name: '',
            ownerID: '',
            ssoProvider: '',
            tier: '',
            tierID: 1
        };
    }

    /**
     * Lb history update.
     * @param state
     * @private
     */
    private lbHistoryUpdate(state: boolean): void {
        if (typeof state !== 'boolean') {
            this.logService.debug('PortalResolverGuard: lbHistoryUpdate', 'Invalid state parameter provided');

            return;
        } else {
            const lbHistory: string | null = localStorage.getItem('lbHistory');
            if (!lbHistory) {
                this.logService.debug('PortalResolverGuard: lbHistoryUpdate', 'No lbHistory found in localStorage');

                return;
            }
            const historyObject: any = JSON.parse(lbHistory);
            historyObject['samlEnforced'] = state;
            localStorage.setItem('lbHistory', JSON.stringify(historyObject));
        }
    }
}
